const GA_ID = process.env.REACT_APP_GA_MEASUREMENT_ID;
const META_PIXEL_ID = process.env.REACT_APP_META_PIXEL_ID;

const CONSENT_KEY = "pw_analytics_consent";
const FIRST_TOUCH_KEY = "pw_attribution_first";
const LAST_TOUCH_KEY = "pw_attribution_last";
const TRACKED_PURCHASES_KEY = "pw_tracked_purchases";

let gaLoaded = false;
let pixelLoaded = false;
let lastTrackedPath = null;

const ALLOWED_EVENT_PROPS = new Set([
  "page_path", "page_title", "page_location", "referrer",
  "utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term",
  "button_text", "button_location", "product_name", "product_category", "product_price",
  "currency", "transaction_id", "value", "sponsorship_tier", "inquiry_type", "academy_program",
  "selected_size", "selected_color", "traffic_source", "traffic_medium", "traffic_campaign",
  "traffic_content", "traffic_term", "referring_domain", "device_type", "form_name", "source",
  "label", "landing_page", "quantity", "cart_value", "bundle_name", "product_id",
]);

function safeLocalStorage() {
  try {
    window.localStorage.setItem("__pw_test__", "1");
    window.localStorage.removeItem("__pw_test__");
    return window.localStorage;
  } catch {
    return null;
  }
}

function sanitizeProps(props = {}) {
  const safe = {};
  for (const [k, v] of Object.entries(props)) {
    if (ALLOWED_EVENT_PROPS.has(k) && v !== undefined && v !== null && v !== "") safe[k] = v;
  }
  return safe;
}

function getDeviceType() {
  if (typeof navigator === "undefined") return "unknown";
  return /Mobi|Android/i.test(navigator.userAgent) ? "mobile" : "desktop";
}

// ---------- Consent ----------
export function getConsent() {
  return safeLocalStorage()?.getItem(CONSENT_KEY) || null;
}

function setConsentValue(value) {
  safeLocalStorage()?.setItem(CONSENT_KEY, value);
}

export function acceptConsent() {
  setConsentValue("granted");
  try { window.posthog?.opt_in_capturing(); } catch { /* noop */ }
  initGA4();
  initMetaPixel();
}

export function declineConsent() {
  setConsentValue("denied");
  try { window.posthog?.opt_out_capturing(); } catch { /* noop */ }
}

function applyStoredConsent() {
  const consent = getConsent();
  if (consent === "granted") {
    try { window.posthog?.opt_in_capturing(); } catch { /* noop */ }
    initGA4();
    initMetaPixel();
  } else {
    try { window.posthog?.opt_out_capturing(); } catch { /* noop */ }
  }
}

// ---------- UTM / Attribution ----------
function readUtmFromUrl() {
  const params = new URLSearchParams(window.location.search);
  const utm = {
    utm_source: params.get("utm_source") || undefined,
    utm_medium: params.get("utm_medium") || undefined,
    utm_campaign: params.get("utm_campaign") || undefined,
    utm_content: params.get("utm_content") || undefined,
    utm_term: params.get("utm_term") || undefined,
  };
  return Object.fromEntries(Object.entries(utm).filter(([, v]) => v));
}

function referringDomain() {
  if (!document.referrer) return "direct";
  try { return new URL(document.referrer).hostname; } catch { return "direct"; }
}

export function captureAttribution() {
  const storage = safeLocalStorage();
  if (!storage) return;
  const utm = readUtmFromUrl();
  const hasUtm = Object.keys(utm).length > 0;
  const touch = {
    ...utm,
    referring_domain: referringDomain(),
    landing_page: window.location.pathname,
    timestamp: new Date().toISOString(),
  };
  if (!storage.getItem(FIRST_TOUCH_KEY)) {
    storage.setItem(FIRST_TOUCH_KEY, JSON.stringify(touch));
  }
  if (hasUtm || !storage.getItem(LAST_TOUCH_KEY)) {
    storage.setItem(LAST_TOUCH_KEY, JSON.stringify(touch));
  }
}

export function getAttribution() {
  const storage = safeLocalStorage();
  if (!storage) return { first_touch: null, last_touch: null };
  try {
    return {
      first_touch: JSON.parse(storage.getItem(FIRST_TOUCH_KEY) || "null"),
      last_touch: JSON.parse(storage.getItem(LAST_TOUCH_KEY) || "null"),
    };
  } catch {
    return { first_touch: null, last_touch: null };
  }
}

// ---------- GA4 ----------
export function initGA4() {
  if (gaLoaded || !GA_ID || getConsent() !== "granted") return;
  try {
    gaLoaded = true;
    const s1 = document.createElement("script");
    s1.async = true;
    s1.src = `https://www.googletagmanager.com/gtag/js?id=${GA_ID}`;
    document.head.appendChild(s1);
    window.dataLayer = window.dataLayer || [];
    window.gtag = function gtag() { window.dataLayer.push(arguments); };
    window.gtag("js", new Date());
    window.gtag("config", GA_ID, { send_page_view: false });
  } catch { /* never block the app */ }
}

// ---------- Meta Pixel (prepared, blank/off unless configured) ----------
export function initMetaPixel() {
  if (pixelLoaded || !META_PIXEL_ID || getConsent() !== "granted") return;
  try {
    pixelLoaded = true;
    /* eslint-disable */
    (function (f, b, e, v, n, t, s) {
      if (f.fbq) return; n = f.fbq = function () { n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments); };
      if (!f._fbq) f._fbq = n; n.push = n; n.loaded = !0; n.version = "2.0"; n.queue = [];
      t = b.createElement(e); t.async = !0; t.src = v;
      s = b.getElementsByTagName(e)[0]; s.parentNode.insertBefore(t, s);
    })(window, document, "script", "https://connect.facebook.net/en_US/fbevents.js");
    /* eslint-enable */
    window.fbq("init", META_PIXEL_ID);
    window.fbq("track", "PageView");
  } catch { /* never block the app */ }
}

export function trackMetaEvent(name, params = {}) {
  if (!pixelLoaded || !window.fbq) return;
  try { window.fbq("track", name, sanitizeProps(params)); } catch { /* noop */ }
}

// ---------- Page views ----------
export function trackPageView(path) {
  try {
    if (!path || path === lastTrackedPath) return;
    lastTrackedPath = path;
    const { last_touch } = getAttribution();
    const props = sanitizeProps({
      page_path: path,
      page_title: document.title,
      page_location: window.location.href,
      referrer: document.referrer || "direct",
      utm_source: last_touch?.utm_source,
      utm_medium: last_touch?.utm_medium,
      utm_campaign: last_touch?.utm_campaign,
      utm_content: last_touch?.utm_content,
      utm_term: last_touch?.utm_term,
    });
    if (window.gtag && GA_ID) window.gtag("event", "page_view", props);
    try { window.posthog?.capture("$pageview", props); } catch { /* noop */ }
  } catch { /* analytics must never break the site */ }
}

// ---------- Standard event tracking ----------
export function trackEvent(name, properties = {}) {
  try {
    const { last_touch } = getAttribution();
    const enriched = sanitizeProps({
      device_type: getDeviceType(),
      traffic_source: last_touch?.utm_source,
      traffic_medium: last_touch?.utm_medium,
      traffic_campaign: last_touch?.utm_campaign,
      traffic_content: last_touch?.utm_content,
      traffic_term: last_touch?.utm_term,
      referring_domain: last_touch?.referring_domain,
      ...properties,
    });
    if (window.gtag && GA_ID) window.gtag("event", name, enriched);
    try { window.posthog?.capture(name, enriched); } catch { /* noop */ }
  } catch { /* analytics must never break the site */ }
}

// ---------- Purchase dedupe ----------
export function trackPurchaseOnce({ transactionId, value, currency = "USD", productName, productCategory, eventName = "purchase" }) {
  try {
    if (!transactionId) return;
    const storage = safeLocalStorage();
    let tracked = [];
    if (storage) {
      try { tracked = JSON.parse(storage.getItem(TRACKED_PURCHASES_KEY) || "[]"); } catch { tracked = []; }
    }
    const key = `${eventName}:${transactionId}`;
    if (tracked.includes(key)) return;
    trackEvent(eventName, {
      transaction_id: transactionId,
      value,
      currency,
      product_name: productName,
      product_category: productCategory,
    });
    if (storage) {
      tracked.push(key);
      storage.setItem(TRACKED_PURCHASES_KEY, JSON.stringify(tracked.slice(-200)));
    }
  } catch { /* never block the app */ }
}

// ---------- Backend attribution helper ----------
export function attributionForSubmit() {
  return getAttribution();
}

// ---------- Init ----------
export function initAnalytics() {
  try {
    captureAttribution();
    applyStoredConsent();
  } catch { /* never block the app */ }
}
